'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

interface Props {
	to: string
	children: React.ReactNode
}

export default function ScreenTransition({ to, children }: Props) {
	const [isAnimating, setIsAnimating] = useState(false)
	const [circleStyle, setCircleStyle] = useState<React.CSSProperties>({})
	const [fadeOut, setFadeOut] = useState(false)
	const router = useRouter()

	const handleClick = (e: React.MouseEvent) => {
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		const x = rect.left + rect.width / 2
		const y = rect.top + rect.height / 2

		setCircleStyle({
			top: `${y}px`,
			left: `${x}px`,
		})

		setFadeOut(true)
		setIsAnimating(true)

		setTimeout(() => {
			router.push(to)
		}, 1000)
	}

	useEffect(() => {
		if (isAnimating) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
		return () => {
			document.body.style.overflow = ''
		}
	}, [isAnimating])

	return (
		<>
			<div
				onClick={handleClick}
				className={`inline-block relative z-10 cursor-pointer transition-opacity duration-700 ${
					fadeOut ? 'opacity-0' : 'opacity-100'
				}`}
			>
				{children}
			</div>

			{isAnimating && (
				<div
					className="fixed bg-background-patient rounded-full z-50 animate-slowExpand pointer-events-none"
					style={{
						width: '16px',
						height: '16px',
						...circleStyle,
						transform: 'translate(-50%, -50%)',
					}}
				/>
			)}

			<style jsx>{`
				@keyframes slowExpand {
					0% {
						width: 16px;
						height: 16px;
						border-radius: 9999px;
						transform: translate(-50%, -50%) scale(1);
					}
					25% {
						transform: translate(-50%, -50%) scale(1.2);
					}
					50% {
						transform: translate(-50%, -50%) scale(1.5);
					}
					75% {
						transform: translate(-50%, -50%) scale(1.8);
					}
					100% {
						width: 3000px;
						height: 3000px;
						transform: translate(-50%, -50%) scale(2);
						border-radius: 0;
					}
				}

				.animate-slowExpand {
					animation: slowExpand 1s cubic-bezier(0.77, 0, 0.175, 1) forwards;
				}
			`}</style>
		</>
	)
}
